"use client"

import React, { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ChevronsUpDown } from "lucide-react"
import { Chip } from "@/components/explorer/active-filter-chips"
import { useExplorer } from "@/hooks/use-explorer"
import {
  ANY_VALUE,
  DEFAULT_OPERATOR,
  operatorLabel,
  REFERENCE_TYPEAHEAD_THRESHOLD,
  fromDateTimeLocal,
  isValuelessOperator,
  toDateTimeLocal,
  type FilterValueState,
} from "@/lib/filter-values"
import { useTranslation, type Translator } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { AtsFilterDefinition, AtsReferenceDataItem } from "@/types/ats-explorer-api"

const TYPEAHEAD_DEBOUNCE_MS = 300

/**
 * Loads the reference sets the given definitions need (once per type, failure-evicting)
 * and returns them keyed by reference type.
 */
export function useReferenceSets(atsSystem: string, definitions: AtsFilterDefinition[]): Record<string, AtsReferenceDataItem[]> {
  const { loadReferenceData } = useExplorer()
  const [sets, setSets] = useState<Record<string, AtsReferenceDataItem[]>>({})
  const requested = useRef<Set<string>>(new Set())

  const needed = Array.from(new Set(definitions.map((d) => d.referenceType).filter((t): t is string => !!t)))
  const neededKey = needed.join("|")

  useEffect(() => {
    needed
      .filter((type) => !requested.current.has(type))
      .forEach((type) => {
        requested.current.add(type)
        loadReferenceData(atsSystem, type)
          .then((items) => setSets((current) => ({ ...current, [type]: items })))
          .catch(() => {
            requested.current.delete(type)
            setSets((current) => ({ ...current, [type]: [] }))
          })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neededKey, atsSystem])

  return sets
}

export function OperatorSelect({ definition, value, onChange, className }: { definition: AtsFilterDefinition; value: string; onChange: (operator: string) => void; className?: string }) {
  const { t } = useTranslation()
  const operators = definition.supportedOperators?.length ? definition.supportedOperators : [DEFAULT_OPERATOR]
  if (operators.length <= 1) return null
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className ?? "h-8 w-28 shrink-0 text-xs"}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {operators.map((op) => (
          <SelectItem key={op} value={op}>
            {operatorLabel(op, t)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export interface FilterValueControlProps {
  atsSystem: string
  definition: AtsFilterDefinition
  state: FilterValueState | undefined
  operator: string
  referenceItems: AtsReferenceDataItem[]
  /** Replace the filter's values; labels are the display names of chosen reference ids */
  onChange: (values: string[], labels?: Record<string, string>) => void
  /** Raw text kept while typing a comma-separated list */
  draft?: string
  onDraftChange?: (draft: string) => void
}

/**
 * The value editor for one filter: reference picker (typeahead for clamped sets),
 * yes/no select, suggestion input for pick lists, date-time input, comma list, or a
 * plain input. Value-less operators render a note instead.
 */
export function FilterValueControl({ atsSystem, definition, state, operator, referenceItems, onChange, draft, onDraftChange }: FilterValueControlProps) {
  const { loadReferenceData } = useExplorer()
  const { t } = useTranslation()
  const current = state?.values ?? []
  const referenceType = definition.referenceType ?? ""

  if (isValuelessOperator(operator)) {
    return <div className="flex h-8 items-center px-2 text-xs italic text-muted-foreground">{t("explorer.filterControls.noValueNeeded")}</div>
  }

  if (definition.dataType === "reference") {
    return (
      <ReferenceValuePicker
        items={referenceItems}
        multiple={definition.allowsMultiple}
        selected={current}
        knownLabels={state?.valueLabels}
        onChange={onChange}
        search={(query) => loadReferenceData(atsSystem, referenceType, query)}
      />
    )
  }

  if (definition.dataType === "boolean") {
    return (
      <Select value={current[0] ?? ANY_VALUE} onValueChange={(value) => onChange([value])}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY_VALUE}>{t("explorer.filterControls.any")}</SelectItem>
          <SelectItem value="true">{t("common.state.yes")}</SelectItem>
          <SelectItem value="false">{t("common.state.no")}</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  if (definition.referenceType) {
    return <SuggestionInput suggestions={referenceItems} values={current} multiple={definition.allowsMultiple} onChange={onChange} />
  }

  if (definition.dataType === "datetime") {
    return (
      <Input
        type="datetime-local"
        className="h-8 text-xs"
        value={toDateTimeLocal(current[0])}
        onChange={(e) => onChange([fromDateTimeLocal(e.target.value)])}
      />
    )
  }

  if (definition.allowsMultiple) {
    return (
      <Input
        className="h-8 text-xs"
        placeholder={t("explorer.filterControls.commaSeparated")}
        value={state ? (draft ?? current.join(", ")) : ""}
        onChange={(e) => {
          onDraftChange?.(e.target.value)
          onChange(e.target.value.split(",").map((v) => v.trim()))
        }}
      />
    )
  }

  return (
    <Input
      type={definition.dataType === "number" ? "number" : "text"}
      className="h-8 text-xs"
      placeholder={t("explorer.filterControls.any")}
      value={current[0] ?? ""}
      onChange={(e) => onChange([e.target.value])}
    />
  )
}

interface SuggestionInputProps {
  suggestions: AtsReferenceDataItem[]
  values: string[]
  onChange: (values: string[]) => void
  /** Several values at once, shown as removable chips; one value otherwise */
  multiple?: boolean
}

/**
 * Free-text value input with a suggestion dropdown when the vendor declares
 * options for the field (suggestions are sent by id; free text verbatim). A
 * multi-value filter keeps the list open and collects the chosen values as chips.
 */
export function SuggestionInput({ suggestions, values, onChange, multiple = false }: SuggestionInputProps) {
  const { t, n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const select = (next: string) => {
    if (!multiple) {
      onChange([next])
      setOpen(false)
    } else if (values.includes(next)) {
      onChange(values.filter((value) => value !== next))
    } else {
      onChange([...values, next])
    }
    setSearch("")
  }

  const showCustomItem = search.trim() !== "" && !suggestions.some((s) => s.id === search.trim())
  const nameOf = (value: string) => suggestions.find((item) => item.id === value)?.name ?? value
  const summary = values.length === 0 ? t("explorer.filterControls.any") : values.length <= 2 ? values.map(nameOf).join(", ") : t("explorer.filterControls.selected", { count: n(values.length) })

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-8 w-full justify-between text-xs font-normal">
            <span className={values.length > 0 ? "truncate" : "truncate text-muted-foreground"}>{summary}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command>
            <CommandInput placeholder={t("explorer.filterControls.pickOrType")} className="h-8 text-xs" value={search} onValueChange={setSearch} />
            <CommandList>
              <CommandEmpty>{t("explorer.filterControls.noSuggestions")}</CommandEmpty>
              <CommandGroup>
                {values.length > 0 && (
                  <CommandItem value="__clear__" onSelect={() => { onChange([]); setOpen(false) }} className="text-xs text-muted-foreground">
                    {t((multiple ? "explorer.filterControls.clearSelection" : "explorer.filterControls.clearValue"))}
                  </CommandItem>
                )}
                {showCustomItem && (
                  <CommandItem value={search} onSelect={() => select(search.trim())} className="text-xs">
                    {t("explorer.filterControls.useValue", { value: search.trim() })}
                  </CommandItem>
                )}
                {suggestions.map((item) => (
                  <CommandItem key={item.id} value={item.name} onSelect={() => select(item.id)} className="gap-2 text-xs">
                    {multiple && <Checkbox checked={values.includes(item.id)} className="pointer-events-none" />}
                    {item.name}
                    {item.name !== item.id && <span className="ml-1 text-muted-foreground">({item.id})</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {multiple && values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((value) => (
            <Chip key={value} label={nameOf(value)} onRemove={() => select(value)} />
          ))}
        </div>
      )}
    </div>
  )
}

interface ReferenceValuePickerProps {
  /** The unfiltered set (possibly clamped by the vendor) */
  items: AtsReferenceDataItem[]
  multiple: boolean
  selected: string[]
  knownLabels?: Record<string, string>
  onChange: (selected: string[], labels: Record<string, string>) => void
  /** Server-side narrowing, used once the unfiltered set hits the vendor clamp */
  search: (query: string) => Promise<AtsReferenceDataItem[]>
}

/**
 * Searchable reference picker. Small sets filter client-side (cmdk); a set that
 * came back at the vendor clamp is incomplete, so typing asks the server for
 * matches instead. Names of chosen items are reported with the selection so chips
 * and the picker keep showing labels after the results change.
 */
export function ReferenceValuePicker({ items, multiple, selected, knownLabels, onChange, search }: ReferenceValuePickerProps) {
  const { t, n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<AtsReferenceDataItem[] | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [names, setNames] = useState<Record<string, string>>(knownLabels ?? {})
  const useTypeahead = items.length >= REFERENCE_TYPEAHEAD_THRESHOLD

  useEffect(() => {
    setNames((current) => ({ ...current, ...Object.fromEntries(items.map((item) => [item.id, item.name])) }))
  }, [items])

  useEffect(() => {
    if (!useTypeahead || query.trim() === "") {
      setResults(null)
      return
    }
    let cancelled = false
    setIsSearching(true)
    const handle = setTimeout(() => {
      search(query)
        .then((found) => {
          if (cancelled) return
          setResults(found)
          setNames((current) => ({ ...current, ...Object.fromEntries(found.map((item) => [item.id, item.name])) }))
        })
        .catch(() => {
          if (!cancelled) setResults([])
        })
        .finally(() => {
          if (!cancelled) setIsSearching(false)
        })
    }, TYPEAHEAD_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [query, useTypeahead, search])

  const visibleItems = results ?? items
  const labelsFor = (ids: string[]) => Object.fromEntries(ids.map((id) => [id, names[id] ?? id]))

  const toggle = (id: string) => {
    const next = !multiple ? (selected[0] === id ? [] : [id]) : selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id]
    onChange(next, labelsFor(next))
    if (!multiple) setOpen(false)
  }

  const selectedNames = selected.map((id) => names[id] ?? id)
  const summary = summaryOf(selectedNames, t, n)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-8 w-full justify-between text-xs font-normal">
          <span className={selected.length === 0 ? "truncate text-muted-foreground" : "truncate"}>{summary}</span>
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        {/* cmdk's own filtering is switched off in typeahead mode: the server already matched */}
        <Command shouldFilter={!useTypeahead}>
          <CommandInput placeholder={t((useTypeahead ? "explorer.filterControls.typeToSearch" : "explorer.filterControls.searchValues"))} className="h-8 text-xs" value={query} onValueChange={setQuery} />
          <CommandList className="max-h-64">
            <CommandEmpty>
              {isSearching
                ? t("explorer.filterControls.searching")
                : useTypeahead && query.trim() === ""
                  ? t("explorer.filterControls.showingFirst", { count: n(items.length) })
                  : t("explorer.common.noValuesFound")}
            </CommandEmpty>
            <CommandGroup>
              {visibleItems.map((item) => (
                <CommandItem key={item.id} value={`${item.name} ${item.id}`} onSelect={() => toggle(item.id)} className="gap-2 text-xs">
                  {multiple && <Checkbox checked={selected.includes(item.id)} className="pointer-events-none" />}
                  <span className="truncate">{item.name}</span>
                  {item.name !== item.id && <span className="ml-auto pl-2 text-[10px] text-muted-foreground">{item.id}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {selected.length > 0 && (
            <div className="border-t p-1">
              <Button variant="ghost" size="sm" className="h-7 w-full text-xs" onClick={() => onChange([], {})}>
                {t("explorer.filterControls.clearSelection")}
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** The picker's trigger text: the chosen names while they fit, else how many are chosen */
function summaryOf(names: string[], t: Translator["t"], n: Translator["n"]): string {
  if (names.length === 0) return t("explorer.filterControls.any")
  if (names.length <= 2) return names.join(", ")
  return t("explorer.filterControls.selected", { count: n(names.length) })
}
